import type { DashboardPendingQuote } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { formatElapsed, PENDING_QUOTE_STATUS_LABELS } from '@/entities/dashboard';
import { koreanDate } from '@/shared/lib';
import { ContentCard, EmptyState, StatusBadge, tableStyles } from '@/shared/ui';

interface DashboardPendingQuotesProps {
  readonly items: readonly DashboardPendingQuote[];
  /** 경과 계산의 기준 시각. */
  readonly now: Date;
}

const LINK_CLASS = 'text-sm font-semibold underline underline-offset-4 hover:no-underline';

/** 미처리 상담 — 오래 기다린 순 5건. 회사명이 없으면 담당자 이름을 보인다. 견적 번호가 상세로 간다. */
export const DashboardPendingQuotesWidget = ({
  items,
  now,
}: DashboardPendingQuotesProps): ReactElement => (
  <ContentCard
    title="미처리 상담"
    description="오래 기다린 순입니다. 수정 요청은 요청이 바뀐 시각부터 셉니다."
    actions={
      <Link to="/quotes" className={LINK_CLASS}>
        전체 보기 →
      </Link>
    }
  >
    {items.length === 0 ? (
      <EmptyState>처리할 상담이 없습니다.</EmptyState>
    ) : (
      <div className="overflow-x-auto">
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              <th className={tableStyles.th}>접수일</th>
              <th className={tableStyles.th}>회사명</th>
              <th className={tableStyles.th}>담당자</th>
              <th className={tableStyles.th}>견적 번호</th>
              <th className={tableStyles.th}>상태</th>
              <th className={tableStyles.th}>경과</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const status = PENDING_QUOTE_STATUS_LABELS[item.status];
              return (
                <tr key={item.quoteRequestId} className={tableStyles.row}>
                  <td className={`${tableStyles.td} tabular-nums`}>
                    {koreanDate(item.waitingSince)}
                  </td>
                  <td className={tableStyles.td}>{item.companyName ?? item.contactName}</td>
                  <td className={tableStyles.td}>{item.contactName}</td>
                  <td className={tableStyles.td}>
                    <Link to={`/quotes/${item.quoteRequestId}`} className={LINK_CLASS}>
                      {item.quoteNumber}
                    </Link>
                  </td>
                  <td className={tableStyles.td}>
                    <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                  </td>
                  <td className={`${tableStyles.td} tabular-nums`}>
                    {formatElapsed(item.waitingSince, now)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </ContentCard>
);
